<script setup lang="ts">
import { CircleUserRoundIcon } from 'lucide-vue-next'
import { computed, ref } from 'vue'

interface Props {
  src?: string
  alt?: string
  placeholder?: string
  status?: 'online' | 'offline'
  size?: string
  shape?: 'circle' | 'square'
  showTooltip?: boolean
  isAvatarInGroup?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  size: 'w-10',
  shape: 'square',
  showTooltip: true,
  isAvatarInGroup: false,
})

const typeMapping = {
  circle: 'rounded-full',
  square: 'rounded-xl',
}

const statusClasses = computed(() => ({
  'avatar-online': props.status === 'online',
  'avatar-offline': props.status === 'offline',
}))

const hasPlaceholder = computed(() => !props.src && props.placeholder)

const initials = computed(() => {
  if (!props.placeholder)
    return ''
  return props.placeholder
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
})

const isValidImage = ref(true)

function handleError() {
  isValidImage.value = false
}
</script>

<template>
  <Tooltip v-if="src && showTooltip && isValidImage" :delay="200">
    <template #tooltip-content>
      <div class="avatar">
        <div :class="[typeMapping[shape]]" class="h-32 w-32">
          <img
            :src="src"
            :alt="alt"
          />
        </div>
      </div>
    </template>
    <div class="avatar" :class="[statusClasses]">
      <div :class="[typeMapping[shape], size, { 'border-2 border-base-100': isAvatarInGroup }]">
        <img
          v-if="src"
          :src="src"
          :alt="alt"
          @error="handleError"
        />
        <CircleUserRoundIcon
          v-else
          :size="32"
          :stroke-width="1.25"
        />
      </div>
    </div>
  </Tooltip>

  <div
    v-else
    class="avatar"
    :class="[statusClasses]"
  >
    <div :class="[typeMapping[shape], size, { 'border-2 border-base-100': isAvatarInGroup }]" class="bg-base-300">
      <template v-if="hasPlaceholder">
        <div class="placeholder bg-neutral text-neutral-content flex h-full items-center justify-center">
          <slot>
            <span>{{ initials }}</span>
          </slot>
        </div>
      </template>
      <template v-else>
        <img
          v-if="src"
          :src="src"
          :alt="alt"
          @error="handleError"
        />
      </template>
    </div>
  </div>
</template>
